import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import CardNB from '../../components/ui-nb/CardNB';
import ButtonNB from '../../components/ui-nb/ButtonNB';
import IconNB from '../../components/ui-nb/IconNB';
import StatusDropdown from '../../components/technician/StatusDropdown';
import StatusTimeline from '../../components/technician/StatusTimeline';
import PhotoUploader from '../../components/technician/PhotoUploader';
import CustomerInfo from '../../components/technician/CustomerInfo';
import { useOrderQuery } from '../../hooks/useOrderQuery';
import { getErrorMessage } from '../../lib/errors';
import { useAuthStore } from '../../stores/authStore';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../components/ToastNB';
import type { Order } from '../../hooks/useOrderQuery';
import type { Tables } from '@tellowup/db';
import { debug, error as logError } from '../../lib/logger';

// Interface for orders with internal_notes (not in generated types)
interface OrderWithNotes extends Order {
  internal_notes?: string;
}

const AVAILABLE_STATUSES = [
  { id: 'status-1', name: 'Received', position: 1 },
  { id: 'status-2', name: 'Diagnosing', position: 2 },
  { id: 'status-3', name: 'Waiting for part', position: 3 },
  { id: 'status-4', name: 'In repair', position: 4 },
  { id: 'status-5', name: 'Testing', position: 5 },
  { id: 'status-6', name: 'Ready for delivery', position: 6 },
  { id: 'status-7', name: 'Delivered', position: 7 },
];

export default function EquipmentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  const userId = user?.id;
  const { order, isLoading, error, refetch } = useOrderQuery(id || null);
  const [internalNotes, setInternalNotes] = useState('');
  const [notesSaved, setNotesSaved] = useState(false);
  const [showPartModal, setShowPartModal] = useState(false);
  const [partDescription, setPartDescription] = useState('');
  const [partCost, setPartCost] = useState('');
  const [partRequestLoading, setPartRequestLoading] = useState(false);
  const [partRequestSuccess, setPartRequestSuccess] = useState(false);
  const [statusConfig, setStatusConfig] = useState<typeof AVAILABLE_STATUSES>([]);
  const [takeEquipmentLoading, setTakeEquipmentLoading] = useState(false);
  const [takeEquipmentError, setTakeEquipmentError] = useState<string | null>(null);
  const [spareParts, setSpareParts] = useState<Tables<'spare_parts'>[]>([]);
  const { addToast } = useToast();

  // Auth protection
  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  // Fetch available statuses from DB
  useEffect(() => {
    async function fetchStatuses() {
      const { data } = await supabase
        .from('status_config')
        .select('id, name, position')
        .order('position', { ascending: true });
      if (data) setStatusConfig(data);
    }
    fetchStatuses();
  }, []);

  // Fetch spare parts linked to this order
  useEffect(() => {
    if (!id) return;
    async function fetchSpareParts() {
      const { data } = await supabase
        .from('spare_parts')
        .select('*')
        .eq('order_id', id)
        .order('created_at', { ascending: false });
      if (data) setSpareParts(data);
    }
    fetchSpareParts();
  }, [id]);

  // Load internal notes when order loads
  useEffect(() => {
    if (order) {
      setInternalNotes((order as OrderWithNotes).internal_notes || '');
    }
  }, [order]);

  // Save internal notes on blur
  const handleNotesBlur = async () => {
    if (!order) return;
    if ((order as OrderWithNotes).internal_notes === internalNotes) return;

    const { error: updateError } = await supabase
      .from('orders')
      .update({ internal_notes: internalNotes })
      .eq('id', order.id);

    if (!updateError) {
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2000);
    }
  };

  // Submit part request
  const handlePartRequest = async () => {
    if (!order || !partDescription.trim()) return;

    setPartRequestLoading(true);
    try {
      const { error: invokeError } = await supabase.functions.invoke('notify-part-request', {
        body: {
          order_id: order.id,
          description: partDescription.trim(),
          cost: partCost ? parseFloat(partCost) : undefined,
          userId,
        },
      });

      if (invokeError) throw invokeError;

      setPartRequestSuccess(true);
      setShowPartModal(false);
      setPartDescription('');
      setPartCost('');
      setTimeout(() => setPartRequestSuccess(false), 3000);
    } catch (err) {
      logError('Part request error', { error: getErrorMessage(err) });
    } finally {
      setPartRequestLoading(false);
    }
  };

  // Take equipment handler
  const handleTakeEquipment = async () => {
    if (!order || !userId) return;

    setTakeEquipmentLoading(true);
    setTakeEquipmentError(null);

    try {
      // Get "In repair" status ID (position 4)
      const { data: statusData, error: statusError } = await supabase
        .from('status_config')
        .select('id')
        .eq('position', 4)
        .eq('is_active', true)
        .single();

      if (statusError || !statusData) {
        throw new Error('Could not find "In repair" status');
      }

      // Call notify-status-change to transition to "In repair"
      const { error: invokeError } = await supabase.functions.invoke('notify-status-change', {
        body: {
          order_id: order.id,
          new_status_id: statusData.id,
          changed_by: userId,
        },
      });

      if (invokeError) throw invokeError;

      // Also update assigned_to directly to ensure it's set
      await supabase
        .from('orders')
        .update({ assigned_to: userId })
        .eq('id', order.id);

      // Refresh the order data
      refetch();
    } catch (err) {
      logError('Take equipment error', { error: getErrorMessage(err) });
      addToast(getErrorMessage(err) || 'Failed to take equipment', 'error');
    } finally {
      setTakeEquipmentLoading(false);
    }
  };

  const currentPosition = AVAILABLE_STATUSES.find(s => s.id === order?.status_id)?.position || 0;
  const isTerminal = currentPosition >= 6;

  // Compute spare parts blocking status
  const pendingSpareParts = spareParts.filter(
    (p) => p.status === 'sin_pedir' || p.status === 'pedido'
  );
  const hasPendingSpareParts = pendingSpareParts.length > 0;
  const BLOCKED_STATUS_NAMES = ['In repair', 'In testing', 'Ready for delivery', 'Delivered'];
  const blockedStatuses = hasPendingSpareParts
    ? BLOCKED_STATUS_NAMES.map(name => ({
        statusName: name,
        reason: `${pendingSpareParts.length} spare part(s) not received`,
      }))
    : [];

  if (!user) {
    return (
      <div className="max-w-5xl mx-auto bg-nb-cream p-8">
        <p className="text-nb-text-muted text-center">Redirecting to login...</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto bg-nb-cream p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-nb-cream rounded w-1/3" />
          <div className="h-64 bg-nb-cream rounded" />
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="max-w-5xl mx-auto bg-nb-cream p-8">
        <CardNB className="p-8 text-center">
          <p className="text-lg font-semibold text-black mb-2">Equipment not found</p>
          <p className="text-nb-text-muted mb-4">
            {error || 'This equipment does not exist or you do not have permission to view it.'}
          </p>
          <Link to="/en/technician">
            <ButtonNB variant="primary" size="md">
              Back to list
            </ButtonNB>
          </Link>
        </CardNB>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto bg-nb-cream">
      {/* Success notification */}
      {partRequestSuccess && (
        <CardNB className="mb-4 p-4 flex items-center gap-2 bg-nb-black text-white border-2 border-black">
          <span>✓</span>
          <span>Part request sent successfully</span>
        </CardNB>
      )}

      {/* Back button */}
      <div className="mb-6">
        <Link to="/en/technician" className="text-sm flex items-center gap-1 text-nb-black">
          <IconNB name="chevronLeft" size={16} />
          Back to equipment
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Equipment info */}
          <CardNB className="p-6">
            <h1 className="text-2xl font-bold text-black font-nb mb-1">
              {order.device_brand} {order.device_model || order.device}
            </h1>
            <p className="text-sm mb-4 text-nb-text-muted">Folio: {order.release_code}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {order.profiles && (
                <div className="md:col-span-2">
                  <CustomerInfo profile={order.profiles} />
                </div>
              )}
              <div>
                <p className="text-nb-text-muted">Type</p>
                <p className="font-medium text-black">{order.device}</p>
              </div>
              <div>
                <p className="text-nb-text-muted">Brand</p>
                <p className="font-medium text-black">{order.device_brand || 'N/A'}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-nb-text-muted">Problem description</p>
                <p className="text-black">{order.failure_description || order.issue || 'N/A'}</p>
              </div>
              <div>
                <p className="text-nb-text-muted">Reception date</p>
                <p className="font-medium text-black">
                  {order.created_at
                    ? new Date(order.created_at).toLocaleDateString('es-ES', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })
                    : 'N/A'}
                </p>
              </div>
              {order.imei_serial && (
                <div>
                  <p className="text-nb-text-muted">IMEI/Serial</p>
                  <p className="font-medium text-black font-mono">{order.imei_serial}</p>
                </div>
              )}
            </div>
          </CardNB>

          {/* Status section */}
          <CardNB className="p-6">
            <h2 className="text-lg font-semibold text-black mb-4">Current status</h2>
            {hasPendingSpareParts && (
              <div className="mb-4 px-3 py-2 bg-nb-yellow/15 border-2 border-nb-yellow flex items-center gap-2">
                <span>⚠️</span>
                <span className="text-sm font-medium text-nb-black">
                  {pendingSpareParts.length} spare part(s) pending — "In repair" blocked
                </span>
              </div>
            )}
            <StatusDropdown
              orderId={order.id}
              currentStatusId={order.status_id}
              availableStatuses={statusConfig.length > 0 ? statusConfig : AVAILABLE_STATUSES}
              changedBy={userId || ''}
              disabled={isTerminal}
              onSuccess={refetch}
              blockedStatuses={blockedStatuses}
            />
          </CardNB>

          {/* Progress photos */}
          <CardNB className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-black">Progress photos</h2>
            </div>
            <PhotoUploader
              orderId={order.id}
              bucket="order-photos"
              maxPhotos={5}
              currentPhotoCount={0}
              onUploadComplete={(urls) => {
                debug('Photos uploaded', { urls });
              }}
            />
          </CardNB>

          {/* Internal notes */}
          <CardNB className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-black">Internal notes</h2>
              {notesSaved && (
                <span className="text-xs text-nb-black">Saved</span>
              )}
            </div>
            <textarea
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              onBlur={handleNotesBlur}
              placeholder="Add internal notes about this equipment..."
              className="w-full px-3 py-2 resize-none bg-white border-2 border-black text-black focus:outline-none focus:border-nb-yellow"
              rows={4}
            />
          </CardNB>

          {/* Part request */}
          <CardNB className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-black">Part request</h2>
              <ButtonNB
                variant="secondary"
                size="sm"
                onClick={() => setShowPartModal(true)}
              >
                Request part
              </ButtonNB>
            </div>
            <p className="text-sm text-nb-text-muted">
              Request replacement parts for this equipment.
            </p>
          </CardNB>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          <CardNB className="p-6">
            <h2 className="text-lg font-semibold text-black mb-4">Actions</h2>
            <div className="space-y-3">
              {currentPosition === 1 && (
                <>
                  <ButtonNB
                    variant="primary"
                    size="md"
                    onClick={handleTakeEquipment}
                    loading={takeEquipmentLoading}
                    className="w-full"
                  >
                    🔧 Take equipment
                  </ButtonNB>
                  {takeEquipmentError && (
                    <p className="text-xs text-nb-red">{takeEquipmentError}</p>
                  )}
                </>
              )}

              {currentPosition === 6 && (
                <Link to={`/en/technician/deliver-equipment/${order.id}`}>
                  <ButtonNB variant="primary" size="md" className="w-full">
                    📦 Deliver equipment
                  </ButtonNB>
                </Link>
              )}
            </div>
          </CardNB>

          {/* Status timeline */}
          <CardNB className="p-6">
            <h2 className="text-lg font-semibold text-black mb-4">Status history</h2>
            <StatusTimeline
              history={order.status_history || []}
              isLoading={isLoading}
            />
          </CardNB>
        </div>
      </div>

      {/* Part request modal */}
      {showPartModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4 bg-black/50">
          <CardNB className="p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-black mb-4">Request part</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-nb-text-secondary">
                  Part description *
                </label>
                <input
                  type="text"
                  value={partDescription}
                  onChange={(e) => setPartDescription(e.target.value)}
                  placeholder="e.g. iPhone 13 screen replacement"
                  className="w-full px-4 py-2 bg-white border-2 border-black text-black focus:outline-none focus:border-nb-yellow"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-nb-text-secondary">
                  Estimated cost (optional)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-nb-text-muted">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={partCost}
                    onChange={(e) => setPartCost(e.target.value)}
                    placeholder="0.00"
                    className="w-full pl-7 pr-4 py-2 bg-white border-2 border-black text-black focus:outline-none focus:border-nb-yellow"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <ButtonNB
                variant="secondary"
                size="sm"
                onClick={() => {
                  setShowPartModal(false);
                  setPartDescription('');
                  setPartCost('');
                }}
              >
                Cancel
              </ButtonNB>
              <ButtonNB
                variant="primary"
                size="sm"
                onClick={handlePartRequest}
                loading={partRequestLoading}
                disabled={!partDescription.trim()}
              >
                Submit
              </ButtonNB>
            </div>
          </CardNB>
        </div>
      )}
    </div>
  );
}